"use client"

/**
 * Message Agent Test Chat Dialog.
 *
 * Thin wrapper around the shared TestChatDialog that adds messaging-specific
 * context inputs (channel type, contact name) and points at the message agent
 * test-chat endpoint. Tracks accumulated PII from capture_customer_info tool
 * results across turns so the context block stays accurate.
 */

import { useState, useCallback, useRef } from "react"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { MessageSquare, User, Globe } from "lucide-react"
import {
  TestChat as BaseTestChat,
  TestChatDialog as BaseTestChatDialog,
  type TestChatExperience,
  type TestChatProps as BaseTestChatProps,
} from "@/components/agents/shared/test-chat-dialog"
import { usePermissions } from "@/hooks/use-permissions"
import { DEMO_MODE_PERMISSIONS } from "@/lib/auth/permissions.constants"
import type { ChannelType } from "@/lib/agents/messaging/types"
import { isMarkdownChannel } from "@/lib/agents/messaging/channels/channel-text-format"

/**
 * Onboarding hides the channel picker, and its next step points the merchant at Website
 * Chat. Previewing as that channel is what makes the route send the Markdown hint and the
 * bubble render Markdown — an unset channel previewed the agent as no channel at all.
 */
const ONBOARDING_CHANNEL_TYPE: ChannelType = "web_widget"

const CHANNEL_OPTIONS: Array<{ value: ChannelType; label: string }> = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "telegram", label: "Telegram" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "tiktok", label: "TikTok" },
  { value: "web_widget", label: "Web Widget" },
  { value: "web_demo", label: "Web Demo" },
]

interface CustomerInfo {
  name?: string
  email?: string
  phone?: string
}

interface MessageTestChatProps {
  agentId: string
  agentName?: string
  useAiTools?: boolean
  experience?: TestChatExperience
}

interface MessageTestChatDialogProps extends MessageTestChatProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TestChat(props: MessageTestChatProps) {
  const { testChatProps } = useMessageTestChat(props)
  return <BaseTestChat {...testChatProps} />
}

export function TestChatDialog({
  open,
  onOpenChange,
  ...props
}: MessageTestChatDialogProps) {
  const { resetCustomerInfo, testChatProps } = useMessageTestChat(props)
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) resetCustomerInfo()
    onOpenChange(nextOpen)
  }, [onOpenChange, resetCustomerInfo])

  return (
    <BaseTestChatDialog
      {...testChatProps}
      open={open}
      onOpenChange={handleOpenChange}
    />
  )
}

function useMessageTestChat(
  props: MessageTestChatProps,
): {
  resetCustomerInfo: () => void
  testChatProps: BaseTestChatProps
} {
  const { can } = usePermissions()
  const canManageDemo = can(DEMO_MODE_PERMISSIONS.MANAGE)
  const visibleChannelOptions = canManageDemo
    ? CHANNEL_OPTIONS
    : CHANNEL_OPTIONS.filter(o => o.value !== "web_demo")
  const [channelType, setChannelType] = useState<ChannelType | "">(
    props.experience?.kind === "onboarding" ? ONBOARDING_CHANNEL_TYPE : "",
  )
  const [contactName, setContactName] = useState("")
  const [demoDomain, setDemoDomain] = useState("")
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | undefined>()
  // Ref so buildRequestBody closure always sees latest value
  const customerInfoRef = useRef(customerInfo)
  customerInfoRef.current = customerInfo

  const handleResponse = useCallback((data: Record<string, unknown>) => {
    const info = data.customerInfo as CustomerInfo | undefined
    if (info) {
      setCustomerInfo(info)
    }
  }, [])

  const resetCustomerInfo = useCallback(() => setCustomerInfo(undefined), [])
  const experience = getMessageTestChatExperience(
    props.experience,
    resetCustomerInfo,
  )

  return {
    resetCustomerInfo,
    testChatProps: {
      ...props,
      experience,
      apiEndpoint: `/api/agents/messaging/${props.agentId}/test-chat`,
      buildRequestBody: ({ message, history, useTools }) => ({
        message,
        channelType: channelType || undefined,
        contactName: contactName.trim() || undefined,
        shopify_demo_domain: channelType === "web_demo" && demoDomain.trim() ? demoDomain.trim() : undefined,
        customerInfo: customerInfoRef.current,
        history,
        useTools,
      }),
      onResponse: handleResponse,
      // Same value the route reads to decide the prompt hint, so the preview
      // and production agree by construction.
      textFormat: channelType && isMarkdownChannel(channelType) ? "markdown" : "plain",
      inputPlaceholder: "Type a test message... (Press Enter to send)",
      toolsEnabledLabel:
        "AI tools (escalation, sentiment, customer info) enabled.",
      contextInputs: (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <Select
                value={channelType}
                onValueChange={(v) => setChannelType(v as ChannelType)}
              >
                <SelectTrigger className="text-sm h-8">
                  <SelectValue placeholder="Channel type" />
                </SelectTrigger>
                <SelectContent>
                  {visibleChannelOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <Input
                type="text"
                placeholder="Contact name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="text-sm h-8"
              />
            </div>
          </div>
          {channelType === "web_demo" ? (
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <Input
                type="text"
                placeholder="Shopify domain (e.g. store.myshopify.com)"
                value={demoDomain}
                onChange={(e) => setDemoDomain(e.target.value)}
                className="text-sm h-8"
              />
            </div>
          ) : null}
        </div>
      ),
    },
  }
}

function getMessageTestChatExperience(
  experience: TestChatExperience | undefined,
  resetCustomerInfo: () => void,
): TestChatExperience | undefined {
  if (experience?.kind !== "onboarding") return experience

  return {
    ...experience,
    onRestart: () => {
      resetCustomerInfo()
      experience.onRestart?.()
    },
  }
}
